import { Button, Input, Modal, Select } from '@evaneos/b2b-ui/legacy';
import cx from 'classnames';
import { Fragment, useState } from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router';

import attachmentsLabelingPreview from '../../../../../assets/attachments-labeling-preview.png';
import { ExpandableContainer } from '../../../../../design-system/ExpandableContainer/ExpandableContainer';
import { ArrowBack } from '../../../../../design-system/Icons/ArrowBack';
import { AttachFile } from '../../../../../design-system/Icons/AttachFile';
import { Option } from '../../../../../design-system/types';
import { DossierId } from '../../../../../resources/dossier/types';
import {
    useGetTrackedTripProperties,
    useTrackEventAmplitude,
} from '../../../../../shared/analytics';

import style from './AddAttachmentsModal.css';

type Attachment = {
    id: string;
    type: string;
    name: string;
    category: string | null;
};

type Props = {
    inputs: Array<File>;
    isOpen: boolean;
    dossierId: DossierId;
    onClose: () => void;
    onSend: (attachments: Array<Pick<Attachment, 'category'> & { file: File }>) => void;
};

export function AddAttachmentsModal({ inputs, isOpen, dossierId, onClose, onSend }: Props) {
    const { formatMessage } = useIntl();
    const { trackEvent } = useTrackEventAmplitude();
    const trackedTripProperties = useGetTrackedTripProperties(dossierId);

    const [attachments, setAttachments] = useState<Array<Attachment>>(
        inputs.map((input, key) => ({
            id: key.toString(),
            type: (input.name.match(/.([a-z]*)$/) ?? [])[1] ?? '?',
            name: input.name.replace(/\.[a-z]*$/, ''),
            category: '',
        })),
    );
    const canSendAttachments = attachments.every((file) => file.category);

    const handleSend = (attachments: Array<Pick<Attachment, 'category'> & { file: File }>) => {
        onSend(attachments);
        trackEvent((ampli, defaultProperties) => {
            ampli.attachedDocumentSendClicked({
                ...defaultProperties,
                ...trackedTripProperties,
                number_of_quotes: attachments.filter(
                    (item) => item.category === 'before_trip_quotation',
                ).length,
                number_of_before_trip_documents: attachments.filter(
                    (item) => item.category === 'before_trip_other',
                ).length,
                number_of_roadbooks: attachments.filter(
                    (item) => item.category === 'during_trip_roadbook',
                ).length,
                number_of_in_trip_documents: attachments.filter(
                    (item) => item.category === 'during_trip_other',
                ).length,
            });
        });
    };

    const categoriesOptions = [
        {
            label: formatMessage({
                id: 'discussion.add-attachments-modal.categories.before_trip',
            }),
            options: [
                {
                    value: 'before_trip_quotation',
                    label: formatMessage({
                        id: 'discussion.add-attachments-modal.categories.quote',
                    }),
                },
                {
                    value: 'before_trip_other',
                    label: formatMessage({
                        id: 'discussion.add-attachments-modal.categories.other_before_trip',
                    }),
                },
            ],
        },
        {
            label: formatMessage({
                id: 'discussion.add-attachments-modal.categories.during_trip',
            }),
            options: [
                {
                    value: 'during_trip_roadbook',
                    label: formatMessage({
                        id: 'discussion.add-attachments-modal.categories.roadbook',
                    }),
                },
                {
                    value: 'during_trip_other',
                    label: formatMessage({
                        id: 'discussion.add-attachments-modal.categories.other_during_trip',
                    }),
                },
            ],
        },
    ];

    return (
        <Modal
            title={formatMessage({
                id: 'discussion.add-attachments-modal.title',
            })}
            isOpen={isOpen}
            className={style.modal}
            onRequestClose={onClose}
            mainButton={
                <Button
                    data-testid="send-attachments-button"
                    onClick={() => {
                        const toSend = inputs.map((input, key) => ({
                            category: attachments[key].category,
                            file: new File(
                                [input],
                                `${attachments[key].name}.${attachments[key].type}`,
                                {
                                    type: input.type,
                                    lastModified: input.lastModified,
                                },
                            ),
                        }));
                        handleSend(toSend);
                    }}
                    disabled={!canSendAttachments}
                >
                    {formatMessage({
                        id: 'discussion.add-attachments-modal.send_button',
                    })}
                </Button>
            }
        >
            <ExplanationCard />
            <div className={style.lines}>
                {attachments.map((attachment, index) => (
                    <Fragment key={attachment.id}>
                        <div className={style.line}>
                            <div className={style.inputContainer}>
                                <span
                                    className={cx('badge', style.type, {
                                        [style.first]: index === 0,
                                    })}
                                >
                                    {attachment.type}
                                </span>
                                <Input
                                    id={`input-proposal-url-${index}`}
                                    name="input-proposal-url"
                                    onChange={(event) => {
                                        const newName = event.target.value;
                                        setAttachments((prev) =>
                                            prev.map((file) =>
                                                file.id !== attachment.id
                                                    ? file
                                                    : {
                                                          ...file,
                                                          name: newName,
                                                      },
                                            ),
                                        );
                                    }}
                                    wrapperClassName={style.input}
                                    value={attachment.name}
                                    label={
                                        index === 0
                                            ? formatMessage({
                                                  id: 'discussion.add-attachments-modal.attachment_name',
                                              })
                                            : undefined
                                    }
                                />
                            </div>
                            <div
                                className={style.selectContainer}
                                data-testid={`add-attachments_select-${index}`}
                            >
                                <ArrowBack
                                    className={cx(style.arrow, {
                                        [style.first]: index === 0,
                                    })}
                                />
                                <Select
                                    containerClassName={style.select}
                                    label={
                                        index === 0
                                            ? {
                                                  name: 'label name',
                                                  value: formatMessage({
                                                      id: 'discussion.add-attachments-modal.attachment_category',
                                                  }),
                                              }
                                            : undefined
                                    }
                                    onChange={(event: unknown) => {
                                        const newCategory = (event as Option).value;
                                        setAttachments((prev) =>
                                            prev.map((file) =>
                                                file.id !== attachment.id
                                                    ? file
                                                    : {
                                                          ...file,
                                                          category: newCategory,
                                                      },
                                            ),
                                        );
                                    }}
                                    components={{
                                        GroupHeading: ({ children }) => (
                                            <span className={cx('badge', style.groupHeading)}>
                                                {children}
                                            </span>
                                        ),
                                        Option: ({
                                            children,
                                            className,
                                            innerRef,
                                            innerProps,
                                            ...props
                                        }) => (
                                            <div
                                                ref={innerRef}
                                                className={props.cx(
                                                    {
                                                        'option--is-disabled': props.isDisabled,
                                                        'option--is-focused': props.isFocused,
                                                        'option--is-selected': props.isSelected,
                                                    },
                                                    cx(className, 'annotation', style.option),
                                                )}
                                                {...innerProps}
                                            >
                                                {children}
                                            </div>
                                        ),
                                        MenuList: ({ children }) => (
                                            <div className={style.menu}>{children}</div>
                                        ),
                                    }}
                                    placeholder={formatMessage({
                                        id: 'discussion.add-attachments-modal.categories.select',
                                    })}
                                    options={categoriesOptions}
                                    menuPosition="fixed"
                                    minMenuHeight={240}
                                />
                            </div>
                        </div>
                        {index < attachments.length - 1 && <div className={style.divider} />}
                    </Fragment>
                ))}
            </div>
        </Modal>
    );
}

function ExplanationCard() {
    const { dossierId } = useParams<{ dossierId: DossierId }>();
    const { formatMessage } = useIntl();
    const { trackEvent } = useTrackEventAmplitude();
    const trackedTripProperties = useGetTrackedTripProperties(dossierId);

    return (
        <div className={style.explanation}>
            <ExpandableContainer
                classNameContainer={style.expandable}
                classNameButton={style.collapse}
                previewTitle={
                    <h3 className={style.title}>
                        <AttachFile color="var(--ink)" width="20px" height="20px" />
                        {formatMessage({
                            id: 'discussion.add-attachments-modal.explanation.title',
                        })}
                    </h3>
                }
                content={
                    <div className={style.content}>
                        <p>
                            {formatMessage({
                                id: 'discussion.add-attachments-modal.explanation.line_1',
                            })}
                            <br />
                            {formatMessage({
                                id: 'discussion.add-attachments-modal.explanation.line_2',
                            })}
                        </p>
                        <img src={attachmentsLabelingPreview} className={style.image} />
                    </div>
                }
                onClickOnExpand={() =>
                    trackEvent((ampli, defaultProperties) =>
                        ampli.attachedDocumentExplanationToggled({
                            ...defaultProperties,
                            ...trackedTripProperties,
                        }),
                    )
                }
            />
        </div>
    );
}
